import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Table, type Column } from 'grabbitbot-legacy-ui'
import { statisticsClient, tradingClient } from '../../api'
import type { BalanceVsMopRow } from '../../api/statisticsTypes'
import { formatNumberByCoin } from '../trading/coinConfigLabels'
import { isUsd } from '../trading/tradingConstants'
import { failureText } from '../../api/errorMessages'

interface Row extends BalanceVsMopRow {
  price: number
  balanceMopDiff: number
  balanceMopDiffUsdt: number
  stuck: boolean
}

function num(value: unknown): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

/** Legacy `StatisticsStuckCoinsComponent.refreshData`'s per-row derivation. */
function deriveRow(item: BalanceVsMopRow): Row {
  const usd = isUsd(item.coin)
  let price = 0
  let balanceMopDiff = 0
  let balanceMopDiffUsdt = 0
  if (!usd) {
    if (item.balance) price = item.balanceActualUsdt / item.balance
    else if (item.mop) price = item.mopActualAmount / item.mop
    balanceMopDiff = item.balance - item.mop
    balanceMopDiffUsdt = price ? balanceMopDiff * price : item.balanceActualUsdt - item.mopActualAmount
  }
  const stuck = item.mop > item.balance / 1.001
  return { ...item, price, balanceMopDiff, balanceMopDiffUsdt, stuck }
}

function pctCellClass(percent: number, stuck: boolean): string | undefined {
  if (stuck) return undefined
  if (percent < 0) return 'portal-pnl-negative'
  if (percent > 0) return 'portal-pnl-positive'
  return undefined
}

function diffCellClass(diff: number, usd: boolean, stuck: boolean): string | undefined {
  if (usd || stuck) return undefined
  if (diff < 0) return 'portal-pnl-negative'
  if (diff > 0) return 'portal-pnl-positive'
  return undefined
}

/**
 * Equivalent of legacy `StatisticsStuckCoinsComponent` (route
 * `.../statistics/stuck-coins`) — this is legacy's real "Wallet" nav item
 * (`v3-header.component.html` labels the link "Wallet"; the
 * legacy page read contract #17 `coin/balance-vs-mop`, which needs the GCP
 * exchangeinterface the new system does not run). Since 0.14.2 it reads #15
 * `/v3/spot/bot/{ex}/wallet` (same row shape): the balance comes from the
 * spotbot, and the open-position half is filled (it was always empty in #17).
 */
export function StuckCoinsPage() {
  const { exchangeId = '', tradingType = 'spot' } = useParams<{ exchangeId: string; tradingType: string }>()
  if (tradingType !== 'spot') {
    return (
      <div className="portal-card">
        <h1>Wallet</h1>
        <p className="portal-notice portal-notice--warning">Margin trading is not supported.</p>
      </div>
    )
  }
  return <SpotWallet key={exchangeId} exchangeId={exchangeId} tradingType={tradingType} />
}

function SpotWallet({ exchangeId, tradingType }: { exchangeId: string; tradingType: string }) {
  const [rows, setRows] = useState<Row[] | null>(null)
  const [pairsByCoin1, setPairsByCoin1] = useState<Record<string, string[]>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>(null)
  const [expandedCoin, setExpandedCoin] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    statisticsClient
      .wallet(exchangeId)
      .then((result) => {
        if (cancelled) return
        const filtered = result
          .filter((item) => !item.coin.includes('*'))
          .filter((item) => item.mop > 0 || item.balance > 0)
          .map(deriveRow)
        setRows(filtered)
        setError(null)
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err ?? true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    tradingClient
      .searchCoinConfigs(exchangeId)
      .then((configs) => {
        if (cancelled) return
        const map: Record<string, string[]> = {}
        for (const c of configs) (map[c.coin1] ??= []).push(c.coin2)
        setPairsByCoin1(map)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [exchangeId, tradingType])

  const sums = useMemo(() => {
    const list = rows ?? []
    const mopCount = list.reduce((s, r) => s + num(r.mopCount), 0)
    const mopAmount = list.reduce((s, r) => s + num(r.mopAmount), 0)
    const mopActualAmount = list.reduce((s, r) => s + num(r.mopActualAmount), 0)
    const balanceActualUsdt = list.reduce((s, r) => s + num(r.balanceActualUsdt), 0)
    const mopActualLossUsdt = list.reduce((s, r) => s + num(r.mopActualLossUsdt), 0)
    const balanceMopDiffUsdt = list.reduce((s, r) => s + num(r.balanceMopDiffUsdt), 0)
    const percent = mopAmount ? Number(((100 * mopActualLossUsdt) / mopAmount).toFixed(2)) : 0
    return { mopCount, mopAmount, mopActualAmount, balanceActualUsdt, mopActualLossUsdt, balanceMopDiffUsdt, percent }
  }, [rows])

  const columns: Column<Row>[] = [
    {
      key: 'coin',
      label: 'Coin',
      align: 'left',
      sortValue: (r) => r.coin,
      format: (_v, r) => {
        if (isUsd(r.coin)) return r.coin
        const pairs = pairsByCoin1[r.coin] ?? []
        if (pairs.length === 0) return <Link to={`/${exchangeId}/spot/coins/${r.coin}/USDT`}>{r.coin}</Link>
        if (pairs.length === 1) return <Link to={`/${exchangeId}/spot/coins/${r.coin}/${pairs[0]}`}>{r.coin}</Link>
        return (
          <span>
            <button type="button" onClick={() => setExpandedCoin((prev) => (prev === r.coin ? null : r.coin))}>
              {r.coin}
            </button>
            {expandedCoin === r.coin && (
              <ul className="portal-chip-list">
                {pairs.map((c2) => (
                  <li key={c2} className="portal-chip">
                    <Link to={`/${exchangeId}/spot/coins/${r.coin}/${c2}`}>
                      {r.coin}
                      {c2}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </span>
        )
      },
    },
    { key: 'mopCount', label: 'Sum count of MOPs', numeric: true, sortValue: (r) => num(r.mopCount) },
    {
      key: 'mop',
      label: 'Sum value of MOPs (base)',
      numeric: true,
      sortValue: (r) => num(r.mop),
      format: (_v, r) => formatNumberByCoin(num(r.mop), r.coin),
    },
    { key: 'mopAmount', label: 'Sum value of MOPs (USD)', numeric: true, sortValue: (r) => num(r.mopAmount), format: (v) => num(v).toFixed(2) },
    {
      key: 'mopActualAmount',
      label: 'Actual value of MOPs (USD)',
      numeric: true,
      sortValue: (r) => num(r.mopActualAmount),
      format: (v) => num(v).toFixed(2),
    },
    {
      key: 'mopActualLossUsdt',
      label: 'Diff (buy vs actual, USD)',
      numeric: true,
      sortValue: (r) => num(r.mopActualLossUsdt),
      cellClass: (_v, r) => pctCellClass(num(r.mopActualLossUsdtPercent), r.stuck),
      format: (v) => num(v).toFixed(2),
    },
    {
      key: 'mopActualLossUsdtPercent',
      label: 'Diff %',
      numeric: true,
      sortValue: (r) => num(r.mopActualLossUsdtPercent),
      cellClass: (_v, r) => pctCellClass(num(r.mopActualLossUsdtPercent), r.stuck),
      format: (v) => `${num(v) > 0 ? '+' : ''}${num(v)}%`,
    },
    { key: 'balance', label: 'Balance', numeric: true, sortValue: (r) => num(r.balance), format: (_v, r) => formatNumberByCoin(num(r.balance), r.coin) },
    {
      key: 'balanceActualUsdt',
      label: 'Balance (USD)',
      numeric: true,
      sortValue: (r) => num(r.balanceActualUsdt),
      format: (v) => num(v).toFixed(2),
    },
    {
      key: 'balanceMopDiffUsdt',
      label: 'Diff of Balance and MOPs',
      numeric: true,
      sortValue: (r) => num(r.balanceMopDiffUsdt),
      cellClass: (_v, r) => diffCellClass(num(r.balanceMopDiffUsdt), isUsd(r.coin), r.stuck),
      format: (_v, r) =>
        isUsd(r.coin) ? (
          '+ $0'
        ) : (
          <>
            {r.balanceMopDiffUsdt > 0 ? '+' : ''}${num(r.balanceMopDiffUsdt).toFixed(2)}
            <span className="portal-muted-small">
              ({r.balanceMopDiff > 0 ? '+' : ''}
              {num(r.balanceMopDiff).toFixed(8)} {r.coin})
            </span>
          </>
        ),
    },
  ]

  return (
    <div>
      <div className="portal-page-title">
        <h1>Wallet</h1>
      </div>
      <div className="portal-card">
        <p className="portal-notice portal-notice--info">
          This list only shows coins which have non-zero MOP count or balance.
        </p>
        {loading && <p>Loading…</p>}
        {error !== null && <p role="alert">{failureText(error, 'Could not load wallet statistics')}</p>}
        {rows && (
          <div className="portal-table-scroll">
            <Table
              columns={columns}
              rows={rows}
              rowKey={(r) => r.coin}
              initialSort={{ key: 'coin', dir: 'asc' }}
              rowClassName={(r) => (r.stuck ? 'portal-row--stuck' : isUsd(r.coin) ? 'portal-row--usd' : undefined)}
              footer={
                <tr className="portal-summary-row">
                  <td>Sum</td>
                  <td style={{ textAlign: 'right' }}>{sums.mopCount}</td>
                  <td />
                  <td style={{ textAlign: 'right' }}>{sums.mopAmount.toFixed(2)}</td>
                  <td style={{ textAlign: 'right' }}>{sums.mopActualAmount.toFixed(2)}</td>
                  <td style={{ textAlign: 'right' }}>{sums.mopActualLossUsdt.toFixed(2)}</td>
                  <td style={{ textAlign: 'right' }} className={pctCellClass(sums.percent, false)}>
                    {sums.percent > 0 ? '+' : ''}
                    {sums.percent}%
                  </td>
                  <td />
                  <td style={{ textAlign: 'right' }}>{sums.balanceActualUsdt.toFixed(2)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {sums.balanceMopDiffUsdt > 0 ? '+' : ''}${sums.balanceMopDiffUsdt.toFixed(2)}
                  </td>
                </tr>
              }
            />
          </div>
        )}
      </div>
    </div>
  )
}
